import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {UserAddress} from '../../../model/user-address.model';
import {StateInteractor} from '../../../interactors/state.interactor';
import {StateService} from '../../../service/state.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {AddressProofTypeModel} from '../../../model/address-proof-type.model';
import {FileInteractor} from '../../../file-upload/file.interactor';
import {map, startWith} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {CityInteractor} from '../../../interactors/city.interactor';
import {CityService} from '../../../service/city.service';
import {ErrorHandlingComponent} from '../../../error-handling.component';
import {UserAddressService} from '../../../service/user-address.service';
import {Proof} from '../../../model/Proof';
import {Toaster, Type} from '../../../toaster/Toaster';

@Component({
  selector: 'app-update-user-address',
  templateUrl: './update-user-address.component.html',
  styleUrls: ['./update-user-address.component.css'],
  providers: [CityInteractor, CityService, StateInteractor, StateService, FileInteractor]
})
export class UpdateUserAddressComponent extends ErrorHandlingComponent implements OnInit {

  formGroup: FormGroup;
  readonly addressProofTypes = AddressProofTypeModel.getAllAddressProofType();
  readonly AddressProofTypeModel = AddressProofTypeModel;
  filteredCities: Observable<string[]>;
  readonly cities = [];
  readonly states = [];

  isLoading: boolean;
  userId: string;
  address: UserAddress;
  addressProofDocuments: File[] = [];

  constructor(@Inject(MAT_DIALOG_DATA) public data: any, private dialogRef: MatDialogRef<UpdateUserAddressComponent>, private formBuilder: FormBuilder,
              private cityInteractor: CityInteractor, private stateInteractor: StateInteractor, private fileInteractor: FileInteractor,
              private userAddressService: UserAddressService) {
    super();
    this.formGroup = formBuilder.group({
      addressLine: formBuilder.control(null, [Validators.required, Validators.maxLength(100)]),
      addressCity: formBuilder.control(null, [Validators.required]),
      addressState: formBuilder.control(null, [Validators.required]),
      addressPinCode: formBuilder.control(null, [Validators.required, Validators.pattern(/\d{6}/),
        Validators.maxLength(6)]),
      proofType: formBuilder.control(null, [Validators.required]),
      proofDocument: formBuilder.control(null, [Validators.required])
    });

  }

  async ngOnInit(): Promise<void> {
    this.userId = this.data.userId;
    this.resetUserAddress();
    (await this.cityInteractor.getAll()).forEach(city => this.cities.push(city.name));
    (await this.stateInteractor.getAll()).forEach(state => this.states.push(state.name));
    this.cities.sort();
    this.states.sort();
  }

  getCityName() {
    const cityName = this.formGroup.controls.addressCity.value;
    if (cityName?.length < 3) {
      this.filteredCities = null;
    } else {
      this.filteredCities =  this.formGroup.controls.addressCity.valueChanges
          .pipe(startWith(cityName), map((value: string) => this.filterCityNames(value)));
    }
  }

  validateCity() {
    const cityName = this.formGroup.controls.addressCity.value;
    const isCityPresent = !!this.cities.find((city) => city === cityName);
    if (!isCityPresent) {
      this.formGroup.controls.addressCity.setErrors({'cityListError': true});
    }
  }


  onDialogCancel() {
    this.resetUserAddress();
    this.closeDialog(null);
  }
  onAddressProofSelect(event) {
    if (event.target.files && event.target.files.length > 0) {
      this.addressProofDocuments.push(event.target.files[0]);
    } else {
      if (this.addressProofDocuments.length < 1) {
        this.formGroup.controls.proofDocument.setErrors({'required': true});
      }
    }
  }

  onAddressProofRemove(file: File) {
    const objWithIdIndex = this.addressProofDocuments.findIndex((obj) => obj === file);
    if (objWithIdIndex > -1) {
      this.addressProofDocuments.splice(objWithIdIndex, 1);
    }
    if (this.addressProofDocuments.length < 1) {
      this.formGroup.controls.proofDocument.setErrors({'required': true});
    }
  }

  async saveAddress(): Promise<void> {
    if (this.formGroup.valid) {
      try {
        this.isLoading = true;
        this.address.proofs[0].fileIds = [];
        for (const addProofDocument of this.addressProofDocuments) {
          const fileId = await this.fileInteractor.upload(addProofDocument);
          this.address.proofs[0].fileIds.push(fileId);
        }
        const createdAddressId = await this.userAddressService.create(this.address, this.userId);
        Toaster.toast(`New address added`, Type.Success);
        this.closeDialog(createdAddressId);
      } catch (error) {
        Toaster.toast(`Something went wrong while trying to save the address`, Type.Error);
        this.closeDialog(null);
      } finally {
        this.resetUserAddress();
        this.isLoading = false;
      }
    } else {
      Toaster.toast(`Invalid address details`, Type.Error);
    }
  }

  private closeDialog(addressId: string) {
    this.dialogRef.close({data: {addressId: addressId }});
  }

  private resetUserAddress() {
    this.address = new UserAddress(null, null, null, null, [new Proof(null, null, null)]);
  }

  private filterCityNames(val: string): string[] {
    return this.cities.filter(city => city.toLowerCase().startsWith(val?.toLowerCase()));
  }

  protected errorMessages(): object {
    return {
      addressLine: [{
        type: 'required',
        message: 'Please provide your address'
      }, {
        type: 'maxlength',
        message: 'maximum 100 characters'
      }],
      addressCity: [{
        type: 'required',
        message: 'Please select city'
      }, {
        type: 'cityListError',
        message: 'Please select a valid city'
      }],
      addressState: [{
        type: 'required',
        message: 'Please select state'
      }],
      addressPinCode: [{
        type: 'required',
        message: 'Please provide your PIN Code'
      }, {
        type: 'pattern',
        message: 'Please a valid PIN Code'
      }, {
        type: 'maxlength',
        message: 'Please enter a PIN Code of 6 characters'
      }],
      proofType: [{
        type: 'required',
        message: 'Please select address proof type'
      }],
      proofDocument: [{
        type: 'required',
        message: 'Please upload the address proof'
      }],
    };
  }
}
